/**
 * Границы дня и недели в часовом поясе пользователя.
 * Неделя — пн 00:00 … вс 23:59 (SPEC §2).
 *
 * Календарные даты везде хранятся строкой `YYYY-MM-DD` — это «день пользователя»,
 * а не момент времени, поэтому арифметика по датам делается через UTC-полдень,
 * чтобы переходы на летнее время не сдвигали сутки.
 */
const DAY_MS = 86_400_000;
const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const WEEKDAY_FULL = [
    'Понедельник',
    'Вторник',
    'Среда',
    'Четверг',
    'Пятница',
    'Суббота',
    'Воскресенье',
];
const MONTH_GEN = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
];
const MONTH_SHORT = [
    'янв',
    'фев',
    'мар',
    'апр',
    'мая',
    'июн',
    'июл',
    'авг',
    'сент',
    'окт',
    'нояб',
    'дек',
];
const MONTH_NOM = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
];
function pad(n) {
    return n < 10 ? `0${n}` : String(n);
}
/** Смещение часового пояса в миллисекундах для конкретного момента. */
export function tzOffsetMs(timeZone, utcMs) {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const parts = dtf.formatToParts(new Date(utcMs));
    const get = (type) => {
        const found = parts.find((p) => p.type === type);
        return found ? Number(found.value) : 0;
    };
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    return asUtc - utcMs;
}
/** Календарная дата пользователя для момента времени. */
export function toDateOnly(instant, timeZone) {
    const ms = instant instanceof Date ? instant.getTime() : new Date(instant).getTime();
    const local = new Date(ms + tzOffsetMs(timeZone, ms));
    return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}`;
}
/** Локальное «сейчас» пользователя: дата и время HH:MM. */
export function localNow(timeZone, now = new Date()) {
    const ms = now.getTime();
    const local = new Date(ms + tzOffsetMs(timeZone, ms));
    return {
        date: toDateOnly(now, timeZone),
        time: `${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`,
    };
}
/** Момент времени по локальной дате и времени пользователя. */
export function instantFromLocal(date, time, timeZone) {
    const [y, m, d] = date.split('-').map(Number);
    const [hh, mm] = time.split(':').map(Number);
    const guess = Date.UTC(y, m - 1, d, hh || 0, mm || 0);
    let offset = tzOffsetMs(timeZone, guess);
    offset = tzOffsetMs(timeZone, guess - offset);
    return new Date(guess - offset);
}
/** Начало дня пользователя как момент времени (00:00 локально). */
export function dayStartInstant(date, timeZone) {
    return instantFromLocal(date, '00:00', timeZone);
}
/** Конец дня пользователя (следующее 00:00, полуоткрытый интервал). */
export function dayEndInstant(date, timeZone) {
    return dayStartInstant(addDays(date, 1), timeZone);
}
function dateToUtcNoon(date) {
    const [y, m, d] = date.split('-').map(Number);
    return Date.UTC(y, m - 1, d, 12);
}
function utcNoonToDate(ms) {
    const dt = new Date(ms);
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}
export function addDays(date, days) {
    return utcNoonToDate(dateToUtcNoon(date) + days * DAY_MS);
}
export function diffDays(from, to) {
    return Math.round((dateToUtcNoon(to) - dateToUtcNoon(from)) / DAY_MS);
}
/** 1 — понедельник … 7 — воскресенье. */
export function weekday(date) {
    const js = new Date(dateToUtcNoon(date)).getUTCDay();
    return js === 0 ? 7 : js;
}
export function weekStart(date) {
    return addDays(date, 1 - weekday(date));
}
export function weekEnd(date) {
    return addDays(weekStart(date), 6);
}
export function monthStart(date) {
    return `${date.slice(0, 7)}-01`;
}
export function monthEnd(date) {
    const [y, m] = date.split('-').map(Number);
    const next = m === 12 ? `${y + 1}-01-01` : `${y}-${pad(m + 1)}-01`;
    return addDays(next, -1);
}
export function weekDates(date) {
    const start = weekStart(date);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}
export function dayOfMonth(date) {
    return Number(date.slice(8, 10));
}
export function monthIndex(date) {
    return Number(date.slice(5, 7)) - 1;
}
export function weekdayShort(date) {
    return WEEKDAY_SHORT[weekday(date) - 1];
}
export function weekdayFull(date) {
    return WEEKDAY_FULL[weekday(date) - 1];
}
/** «19 сентября» */
export function formatDayMonth(date) {
    return `${dayOfMonth(date)} ${MONTH_GEN[monthIndex(date)]}`;
}
/** «19 сент» */
export function formatDayMonthShort(date) {
    return `${dayOfMonth(date)} ${MONTH_SHORT[monthIndex(date)]}`;
}
/** «Суббота, 19 сентября» */
export function formatFullDate(date) {
    return `${weekdayFull(date)}, ${formatDayMonth(date)}`;
}
/** «сб, 19 сент» */
export function formatShortDate(date) {
    return `${weekdayShort(date).toLowerCase()}, ${formatDayMonthShort(date)}`;
}
/** «14–20 сентября», «28 сент — 4 окт» */
export function formatRange(from, to) {
    if (from.slice(0, 7) === to.slice(0, 7)) {
        return `${dayOfMonth(from)}–${dayOfMonth(to)} ${MONTH_GEN[monthIndex(to)]}`;
    }
    return `${formatDayMonthShort(from)} — ${formatDayMonthShort(to)}`;
}
export function monthName(date) {
    return MONTH_NOM[monthIndex(date)];
}
/** «19.09.2026» */
export function formatDotted(date) {
    return `${date.slice(8, 10)}.${date.slice(5, 7)}.${date.slice(0, 4)}`;
}
/** «19.09.2026» / «19.09» → YYYY-MM-DD; null если не разобрать. */
export function parseDotted(value, fallbackYear) {
    const m = value.trim().match(/^(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?$/);
    if (!m)
        return null;
    const d = Number(m[1]);
    const mo = Number(m[2]);
    let y = m[3] ? Number(m[3]) : fallbackYear;
    if (y < 100)
        y += 2000;
    if (d < 1 || d > 31 || mo < 1 || mo > 12)
        return null;
    return `${y}-${pad(mo)}-${pad(d)}`;
}
/** Разница в минутах между HH:MM; отрицательная трактуется как переход через полночь. */
export function minutesBetween(start, end) {
    const toMin = (t) => {
        const [h, m] = t.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };
    const diff = toMin(end) - toMin(start);
    return diff < 0 ? diff + 24 * 60 : diff;
}
export function addMinutes(time, minutes) {
    const [h, m] = time.split(':').map(Number);
    const total = (((h || 0) * 60 + (m || 0) + minutes) % (24 * 60) + 24 * 60) % (24 * 60);
    return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}
export function isValidTime(time) {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
}
/** Приём пищи по локальному времени (SPEC §5, H6). */
export function mealByTime(time) {
    const hour = Number(time.slice(0, 2));
    if (hour < 11)
        return 'breakfast';
    if (hour < 16)
        return 'lunch';
    if (hour < 21)
        return 'dinner';
    return 'snack';
}
//# sourceMappingURL=date.js.map